import { Button, Tooltip } from "antd";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import LeadService from "../../../../services/Lead.services";
import { initGetLead } from "../../../../store/actions/creators";
import { setError } from "../../../../store/reducers";
import "../styles.scss";
import {
  CloudUploadOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileOutlined,
  PaperClipOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";

const cleanFileUrl = (url: string): string =>
  url
    .replace(
      /_[0-9]{4}-[0-9]{2}-[0-9]{2}(?:%20|\s)[0-9]{2}:[0-9]{2}:[0-9]{2}$/,
      "",
    )
    .replace(/_[0-9]{4}-[0-9]{2}-[0-9]{2}$/, "")
    .trim();

const getFileName = (url: string): string =>
  cleanFileUrl(url).split("/").pop()?.replace(/%20/g, " ") || "Tax file";

const getFileIcon = (url: string) => {
  const lower = url.toLowerCase();
  if (lower.match(/\.pdf(\?|$)/))
    return <FilePdfOutlined style={{ color: "#ef4444", fontSize: 18 }} />;
  if (lower.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/))
    return <FileImageOutlined style={{ color: "#3b82f6", fontSize: 18 }} />;
  return <FileOutlined style={{ color: "#8b5cf6", fontSize: 18 }} />;
};

const FileChip = ({ url }: { url: string }) => {
  const name = getFileName(url);
  const cleaned = cleanFileUrl(url);
  return (
    <div className="submission-file-chip">
      {getFileIcon(url)}
      <Tooltip title={name}>
        <span className="submission-file-chip__name">{name}</span>
      </Tooltip>
      <a
        href={cleaned}
        target="_blank"
        rel="noopener noreferrer"
        className="submission-file-chip__view"
      >
        <EyeOutlined />
      </a>
    </div>
  );
};

const Online = ({ leadData, selectedOption, fetchReviews }: any) => {
  const [selectedTaxFile, setSelectedTaxFile] = useState<File | null>(null);
  const [finalFiles, setFinalFiles] = useState<File[]>([]);
  const [offlineFiles, setOfflineFiles] = useState<string[]>([]);
  const [manuFiles, setManuFiles] = useState<string[]>([]);
  const [submitting1, setSubmitting1] = useState(false);
  const [submitting2, setSubmitting2] = useState(false);
  const [finalInputKey, setFinalInputKey] = useState(0);

  const dispatch = useDispatch();
  const { onlineOfflineSubmission, onlineFinalSubmission } = LeadService;

  useEffect(() => {
    if (leadData?.online_offline_attachments) {
      setOfflineFiles(
        leadData.online_offline_attachments
          .split(",")
          .map((f: string) => f.trim())
          .filter(Boolean),
      );
    }
    if (leadData?.manual_signature_attachments) {
      setManuFiles(
        leadData.manual_signature_attachments
          .split(",")
          .map((f: string) => f.trim())
          .filter(Boolean),
      );
    }
  }, [leadData]);

  const submitOnline = async () => {
    if (!leadData?.lead_id || submitting1) return;
    try {
      setSubmitting1(true);
      const formData = new FormData();
      if (selectedTaxFile) formData.append("files", selectedTaxFile);
      formData.append("leadType", selectedOption);
      const response = await onlineOfflineSubmission(
        leadData.lead_id,
        formData,
      );
      if (response) {
        dispatch(
          setError({
            status: true,
            type: "success",
            message: response?.message,
          }),
        );
        setSelectedTaxFile(null);
        fetchReviews();
        dispatch(initGetLead(leadData.lead_id));
      }
    } catch (err: any) {
      dispatch(setError({ status: true, type: "error", message: err }));
    } finally {
      setSubmitting1(false);
    }
  };

  const finalSubmitOnline = async () => {
    if (!leadData?.lead_id || submitting2) return;
    try {
      setSubmitting2(true);
      const formData = new FormData();
      finalFiles.forEach((file) => formData.append("files", file));
      formData.append("leadType", selectedOption);
      const response = await onlineFinalSubmission(leadData.lead_id, formData);
      if (response) {
        dispatch(
          setError({
            status: true,
            type: "success",
            message: response?.message,
          }),
        );
        setFinalFiles([]);
        setFinalInputKey((k) => k + 1); // reset input so same file can be re-selected
        fetchReviews();
        dispatch(initGetLead(leadData.lead_id));
      }
    } catch (err: any) {
      dispatch(setError({ status: true, type: "error", message: err }));
    } finally {
      setSubmitting2(false);
    }
  };

  if (selectedOption !== "1") return null;

  return (
    <div className="submission-wrapper">
      {/* Upload Tax File */}
      <div className="submission-card">
        <div className="submission-card__header">
          <CloudUploadOutlined className="submission-card__header-icon" />
          <span>Online Tax Submission</span>
        </div>
        <div className="submission-card__body">
          <label className="submission-upload-zone">
            <input
              type="file"
              style={{ display: "none" }}
              onChange={(e) => setSelectedTaxFile(e.target.files?.[0] || null)}
            />
            <PaperClipOutlined style={{ fontSize: 20, color: "#5b6af0" }} />
            <span className="submission-upload-zone__label">
              {selectedTaxFile
                ? selectedTaxFile.name
                : "Click to select Tax file"}
            </span>
            {selectedTaxFile && (
              <span
                className="submission-upload-zone__clear"
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedTaxFile(null);
                }}
              >
                <DeleteOutlined />
              </span>
            )}
          </label>
          <Button
            type="primary"
            className="submission-card__submit"
            loading={submitting1}
            disabled={submitting1}
            onClick={submitOnline}
          >
            Upload
          </Button>
        </div>
      </div>

      {/* Upload Final Tax File */}
      <div className="submission-card">
        <div className="submission-card__header submission-card__header--final">
          <CloudUploadOutlined className="submission-card__header-icon" />
          <span>Upload Final Tax Submission</span>
        </div>
        <div className="submission-card__body">
          <label className="submission-upload-zone">
            <input
              key={finalInputKey}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                const files = Array.from(e.target.files || []) as File[];
                setFinalFiles((prev) => [...prev, ...files]);
              }}
            />
            <PaperClipOutlined style={{ fontSize: 20, color: "#10b981" }} />
            <span className="submission-upload-zone__label">
              {finalFiles.length > 0
                ? `${finalFiles.length} file${finalFiles.length > 1 ? "s" : ""} selected`
                : "Click to select Final Tax files"}
            </span>
          </label>
          {finalFiles.length > 0 && (
            <div className="submission-staged-files">
              {finalFiles.map((f, i) => (
                <div key={i} className="submission-staged-files__chip">
                  <FileOutlined style={{ color: "#10b981" }} />
                  <span>{f.name}</span>
                  <DeleteOutlined
                    style={{ color: "#ef4444", cursor: "pointer" }}
                    onClick={() =>
                      setFinalFiles((prev) =>
                        prev.filter((_, idx) => idx !== i),
                      )
                    }
                  />
                </div>
              ))}
            </div>
          )}
          <Button
            type="primary"
            className="submission-card__submit submission-card__submit--final"
            loading={submitting2}
            disabled={submitting2}
            onClick={finalSubmitOnline}
          >
            Upload Final
          </Button>
        </div>
      </div>

      {/* Existing files */}
      {(offlineFiles.length > 0 ||
        manuFiles.length > 0 ||
        leadData?.online_final_attachments) && (
        <div className="submission-files-section">
          {offlineFiles.length > 0 && (
            <div className="submission-files-group">
              <div className="submission-files-group__label">
                Online Tax Files
              </div>
              <div className="submission-files-group__list">
                {offlineFiles.map((f: string, i: number) => (
                  <FileChip key={i} url={f} />
                ))}
              </div>
            </div>
          )}

          {manuFiles.length > 0 && (
            <div className="submission-files-group">
              <div className="submission-files-group__label">
                Manual Signature
              </div>
              <div className="submission-files-group__list">
                {manuFiles.map((f: string, i: number) => (
                  <FileChip key={i} url={f} />
                ))}
              </div>
            </div>
          )}

          {leadData?.online_final_attachments && (
            <div className="submission-files-group">
              <div className="submission-files-group__label">
                Final Tax Files
              </div>
              <div className="submission-files-group__list">
                {leadData.online_final_attachments
                  .split(",")
                  .map((f: string) => f.trim())
                  .filter(Boolean)
                  .map((f: string, i: number) => (
                    <FileChip key={i} url={String(f)} />
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Online;
